import React, { useState, useCallback } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark } from "@fortawesome/free-solid-svg-icons"
import { QC_BUILDER_DRAG_TYPE } from "stores/queryBuilderStore"

interface BuilderUniverseSelectorProps {
  value: string | null
  onChange: (value: string | null) => void
}

/**
 * Single-value drop slot for the UNIVERSE filter. Matches the Rows / Columns
 * drop zone visual language but accepts only one value at a time — dropping
 * replaces. The user can also type a path manually.
 */
const BuilderUniverseSelector: React.FC<BuilderUniverseSelectorProps> = ({
  value,
  onChange,
}) => {
  const [isDragOver, setIsDragOver] = useState(false)
  const [draftText, setDraftText] = useState("")

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (
      e.dataTransfer.types.includes(QC_BUILDER_DRAG_TYPE) ||
      e.dataTransfer.types.includes("text/plain")
    ) {
      e.preventDefault()
      e.dataTransfer.dropEffect = "copy"
    }
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (
      e.dataTransfer.types.includes(QC_BUILDER_DRAG_TYPE) ||
      e.dataTransfer.types.includes("text/plain")
    ) {
      setIsDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (e.currentTarget === e.target) setIsDragOver(false)
    },
    []
  )

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      setIsDragOver(false)
      const variable =
        e.dataTransfer.getData(QC_BUILDER_DRAG_TYPE) ||
        e.dataTransfer.getData("text/plain")
      if (variable) onChange(variable.trim())
    },
    [onChange]
  )

  return (
    <div className="qc-universe">
      <div
        className="qc-universe__slot"
        data-state={isDragOver ? "active" : value ? "filled" : "empty"}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {value ? (
          <>
            <span className="qc-mono qc-universe__value">{value}</span>
            <button
              type="button"
              className="qc-universe__clear"
              aria-label="Clear filter"
              onClick={() => onChange(null)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </>
        ) : (
          <input
            type="text"
            className="qc-universe__input qc-mono"
            placeholder="No filter. Drag a variable or type a path"
            value={draftText}
            onChange={(e) => setDraftText(e.target.value)}
            onBlur={() => {
              const trimmed = draftText.trim()
              if (trimmed) {
                onChange(trimmed)
                setDraftText("")
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const trimmed = draftText.trim()
                if (trimmed) {
                  onChange(trimmed)
                  setDraftText("")
                }
              }
            }}
          />
        )}
      </div>
    </div>
  )
}

export default BuilderUniverseSelector
